import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import { sendSwipeclockUserRequest } from 'hooks/useShuttle';
import { createSelector } from 'reselect';
import {
  PERMISSION_NOT_GRANTED,
  PermissionLevel,
  PermissionLevelCheck,
  PermissionType,
} from 'data/constants';

const swipeclockUser = createSlice({
  name: 'swipeclockUser',
  initialState: {
    dependencyKey: undefined,
    directReports: [],
    features: [],
    permissions: [],
    permissionsDictionary: {},
    productLevel: undefined,
    roles: [],
    readyToInjectScript: false,
    metrics: {
      user: {},
      site: {},
    },
  },
  reducers: {
    // standard reducer logic, with auto-generated action types per reducer
    setDependencyKey: (state, action) => {
      state.dependencyKey = action.payload;
    },
    setMasterData: (state, action) => {
      const {
        directReports,
        employeeIds,
        features,
        permissions,
        roles,
        productLevel,
      } = action.payload ?? {};

      state.directReports = directReports;
      state.employeeIds = employeeIds;
      state.features = features;
      state.permissions = permissions;
      state.roles = roles;
      state.productLevel = productLevel;
      state.permissionsDictionary = createPermissionsDictionary(permissions);
    },
    setMetricsData: (state, action) => {
      state.metrics = action.payload;
    },
    setReadyToInjectScript: (state, action) => {
      state.readyToInjectScript = action.payload;
    },
  },
});

export const {
  setDependencyKey,
  setMasterData,
  setMetricsData,
  setReadyToInjectScript,
} = swipeclockUser.actions;

export default swipeclockUser.reducer;

export const swipeclockUserRequest = createAsyncThunk(
  'swipeclockUser/prepSwipeclockUserRequest',
  async (event, { getState }) => {
    const user = getState().user ?? {};

    const { metrics, ...masterData } = getState().swipeclockUser ?? {};

    const payload = {
      event,
      masterData,
      metrics,
      user,
    };

    return sendSwipeclockUserRequest(payload);
  }
);

export const swipeclockUserSelector = (state) => state.swipeclockUser;

export const selectFeaturesAndReadyToInjectScript = createSelector(
  swipeclockUserSelector,
  (swipeclockUserSlice) => ({
    features: swipeclockUserSlice.features,
    readyToInjectScript: swipeclockUserSlice.readyToInjectScript,
  })
);

const selectSwipeclockUserState = (state) => state.swipeclockUser;

export const selectFeatures = createSelector(
  selectSwipeclockUserState,
  (scUser) => scUser.features
);

export const selectPermissionsDictionary = createSelector(
  selectSwipeclockUserState,
  (scUser) => scUser.permissionsDictionary
);

/**
 * Create a permissions dictionary for the user.
 * Each permission type is stored as a bitwise integer using PermissionLevelCheck.
 *
 * IE:
 *     const { permissionsDictionary } = swipeclockUser
 *     if (meetsMinimumPermissionLevel(permissionsDictionary.modifySchedule, PermissionLevelCheck.all)) {
 *        // Do something awesome
 *     }
 *
 * Values:
 *   PERMISSION_NOT_GRANTED (0) — permission type absent from API response
 *   PermissionLevelCheck.none  (1) — explicitly denied by server
 *   PermissionLevelCheck.self  (2) — self access only
 *   PermissionLevelCheck.directReports (4)
 *   PermissionLevelCheck.selfAndDirectReports (8)
 *   PermissionLevelCheck.all   (16) — full access
 */
function createPermissionsDictionary(permissions) {
  return Object.keys(PermissionType).reduce((obj, key) => {
    return { ...obj, ...modifyPermissionLevel(key, permissions) };
  }, {});
}

function modifyPermissionLevel(type, permissions) {
  const permissionMatch = (permissions || []).find(
    (x) => x.permissionType === PermissionType[type]
  );

  if (permissionMatch) {
    return { [type]: levelToBit(permissionMatch.permissionLevel) };
  }

  return { [type]: PERMISSION_NOT_GRANTED };
}

function levelToBit(level) {
  const map = {
    [PermissionLevel.none]: PermissionLevelCheck.none,
    [PermissionLevel.self]: PermissionLevelCheck.self,
    [PermissionLevel.directReports]: PermissionLevelCheck.directReports,
    [PermissionLevel.selfAndDirectReports]:
      PermissionLevelCheck.selfAndDirectReports,
    [PermissionLevel.all]: PermissionLevelCheck.all,
  };
  return map[level] ?? PERMISSION_NOT_GRANTED;
}
